import { OAuthStorage } from 'angular-oauth2-oidc';

/**
 * Keys belonging to a single login round-trip rather than to the session.
 *
 * These must stay per-tab: the callback lands in the tab that started the flow,
 * so sharing them lets two tabs logging in at the same time overwrite each
 * other's verifier and break one of the two callbacks.
 */
const FLOW_KEYS = new Set(['nonce', 'PKCE_verifier']);

/** Everything the OAuth client keeps for a session, i.e. what a new tab needs to find. */
const SESSION_KEYS = [
  'access_token',
  'access_token_stored_at',
  'expires_at',
  'refresh_token',
  'id_token',
  'id_token_claims_obj',
  'id_token_expires_at',
  'id_token_stored_at',
  'id_token_hint',
  'granted_scopes',
  'session_state',
];

/**
 * Token storage for the OAuth client.
 *
 * New tokens are stored in `localStorage` so that a session is valid across tabs:
 * a deep link opened via middle-click would otherwise have an empty
 * `sessionStorage` and would need to go through the complete OAuth redirect flow.
 *
 * Existing sessions still in `sessionStorage` are moved over on startup and are
 * read from there as a fallback, so the switch costs no one a re-login.
 */
export class AuthStorage implements OAuthStorage {
  constructor() {
    this.migrateLegacySession();
  }

  /**
   * Moves a session that still lives in `sessionStorage` over in one go.
   *
   * Migrating only on write is not enough: nothing writes a token between two
   * silent refreshes, so until one fires a new tab finds an empty storage and
   * has to run the full OAuth redirect - exactly what the move to
   * `localStorage` was meant to avoid.
   */
  private migrateLegacySession(): void {
    // a session in localStorage is the newer one, never let a stale tab overwrite it
    if (localStorage.getItem('access_token')) return;

    for (const key of SESSION_KEYS) {
      const value = sessionStorage.getItem(key);
      if (value === null) continue;
      localStorage.setItem(key, value);
      sessionStorage.removeItem(key);
    }
  }

  getItem(key: string): string | null {
    if (FLOW_KEYS.has(key)) return sessionStorage.getItem(key);
    // localStorage gewinnt: dort steht nach einem Refresh der aktuellere Wert
    return localStorage.getItem(key) ?? sessionStorage.getItem(key);
  }

  setItem(key: string, data: string): void {
    if (FLOW_KEYS.has(key)) {
      sessionStorage.setItem(key, data);
      return;
    }
    localStorage.setItem(key, data);
    sessionStorage.removeItem(key);
  }

  removeItem(key: string): void {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }
}
